import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  Input,
  Label,
  FormGroup,
  Form,
} from "reactstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  addClientSource,
  getClientSourceDataById,
  updateClientSource,
} from "model/ClientSourceModel";

const clientSourceSchema = Yup.object().shape({
  source: Yup.string().trim().required("Source is required"),
  email: Yup.string().trim().email("Enter a valid email").required("Email is required"),
  apiKey: Yup.string().trim().required("API key is required"),
});

const ClientSourceAddEditModal = ({
  setClientSourceAddEditModalOpen,
  clientSourceAddEditModalOpen,
  getClientSourceList,
  itemId,
}) => {
  const [formKey, setFormKey] = useState(0);
  const [formData, setFormData] = useState({
    source: "",
    email: "",
    apiKey: "",
    status: true,
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateField = async (name, value) => {
    if (!clientSourceSchema.fields[name]) return;
    try {
      await Yup.reach(clientSourceSchema, name).validate(value);
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [name]: err.message }));
    }
  };

  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;
    setFormData((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));
    if (touched[name]) {
      await validateField(name, fieldValue);
    }
  };

  const handleBlur = async (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    await validateField(name, value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitSource();
  };

  const closeModal = () => setClientSourceAddEditModalOpen(false);
  const submitSource = async () => {
    try {
      await clientSourceSchema.validate(formData, { abortEarly: false });
      setErrors({});
    } catch (err) {
      const newErrors = {};
      (err?.inner || []).forEach((error) => {
        newErrors[error.path] = error.message;
      });
      setErrors(newErrors);
      setTouched({
        source: true,
        email: true,
        apiKey: true,
      });
      return;
    }

    const payload = {
      source: formData?.source,
      name: formData?.source,
      title: formData?.source,
      email: formData?.email,
      apiKey: formData?.apiKey,
      status: formData?.status ? "active" : "deactive",
    };

    const response = itemId
      ? await updateClientSource(itemId, payload)
      : await addClientSource(payload);

    if (response?.success) {
      toast.success(response?.message || "Source saved successfully");
      getClientSourceList?.();
      closeModal();
    } else {
      toast.error(response?.message || "Unable to save source");
    }
  };

  useEffect(() => {
    const getSourceById = async () => {
      if (!itemId) {
        setFormData({ source: "", email: "", apiKey: "", status: true });
        setErrors({});
        setTouched({});
        setFormKey((k) => k + 1);
        return;
      }
      const response = await getClientSourceDataById({ Id: itemId });
      const row = response?.data;
      if (!row) return;

      setFormData({
        source: row?.source || row?.name || row?.title || "",
        email: row?.email || "",
        apiKey: row?.apiKey || row?.api_key || "",
        status: row?.status === true || row?.status === "active",
      });
      setErrors({});
      setTouched({});
      setFormKey((k) => k + 1);
    };
    getSourceById();
  }, [itemId]);


  return (
    <Modal
    isOpen={clientSourceAddEditModalOpen}
    toggle={closeModal}
      backdrop="static"
      className="modal-full-height"
      centered
    >
      <ModalBody key={formKey} className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0">Source</h5>
          <Button close onClick={closeModal} />
        </div>
        <hr />

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Source <span className="text-danger">*</span></Label>
            <Input
              type="text"
              name="source"
              placeholder="Source"
              value={formData?.source}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {touched.source && errors.source && (
              <small className="text-danger">{errors.source}</small>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Email <span className="text-danger">*</span></Label>
            <Input
              type="email"
              name="email"
              placeholder="Email"
              value={formData?.email}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {touched.email && errors.email && (
              <small className="text-danger">{errors.email}</small>
            )}
          </FormGroup>

          <FormGroup>
            <Label>API Key <span className="text-danger">*</span></Label>
            <Input
              type="text"
              name="apiKey"
              placeholder="API Key"
              value={formData?.apiKey}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {touched.apiKey && errors.apiKey && (
              <small className="text-danger">{errors.apiKey}</small>
            )}
          </FormGroup>

          {/* <FormGroup>
            <Label>Status <span className="text-danger">*</span></Label>
            <div className="input-box d-flex align-items-center justify-content-between">
              <span className="text-muted">Type</span>
              <div className="d-flex align-items-center">
                <strong className="me-2">
                  {formData?.status ? "Active" : "Deactive"}
                </strong>
                <div className="form-switch">
                  <Input
                    type="switch"
                    name="status"
                    checked={formData?.status}
                    onChange={handleChange}
                    className="form-check-input-padding"
                  />
                </div>
              </div>
            </div>
          </FormGroup> */}

          <div className="text-end mt-3">
            <Button color="primary" type="submit">
              Save
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default ClientSourceAddEditModal;
